import { HOW_IT_WORKS_ART_XML, HOW_IT_WORKS_CARDS } from '../../../shared/howItWorksArt.js'
import { smart } from '../lib/typography'
import { WORKER_SECTION_CARD_XML, WORKER_SECTION_HERO_XML } from '../../../shared/workerSectionArt.js'
import Seo from '../components/Seo'
import MarketingNav from '../components/MarketingNav'
import MarketingFooter from '../components/MarketingFooter'
import PageHero from '../components/ds/PageHero'
import Section from '../components/ds/Section'
import SectionHead, { H2 } from '../components/ds/SectionHead'
import CtaBand from '../components/ds/CtaBand'
import Steps from '../components/ds/Steps'
import Button from '../components/ds/Button'
import Art from '../components/ds/Art'
import FaqAccordion from '../components/ds/FaqAccordion'
import { findWorkJsonLd } from '../lib/landingJsonLd'

// /how-it-works — the Find work trunk’s explainer.
//
// Two sequences, deliberately separate: getting set up (once) and how a shift
// goes (every time). The second one is the app’s OWN four cards, art and words,
// from shared/howItWorksArt.js.

const SETUP = [
  { art: WORKER_SECTION_CARD_XML.account,     title: "Tell us who you are",  body: "Your name, where you’re based and what you do. About two minutes." },
  { art: WORKER_SECTION_CARD_XML.aboutYou,    title: 'Prove it once',        body: 'Photo ID, your right to work and your professional registration. You do this once, not for every shift.' },
  { art: WORKER_SECTION_CARD_XML.gettingPaid, title: 'Add where money goes', body: 'Your bank details, so your pay has somewhere to land.' },
  { art: WORKER_SECTION_CARD_XML.signing,     title: 'Start picking up work', body: "Set where you’ll travel and when you’re free, and the shifts come to you." },
]

const FAQS = [
  { q: 'How long does setting up take?',
    a: "About ten minutes of your time. Checks on your documents can take longer, and we tell you where they’re up to rather than leaving you guessing." },
  { q: 'Do I have to take a shift once I have seen it?',
    a: 'No. You look, and you accept the ones you want. Turning work down costs you nothing.' },
  { q: 'Can I work for the same place again?',
    a: "Yes. Favourite a workplace and you’ll see their shifts first." },
  { q: 'What if I need to cancel?',
    a: "Tell us as early as you can and we’ll cover the gap. Letting a workplace down on the morning is the one thing that affects whether they want you back." },
]

export default function HowItWorks() {
  return (
    <div className="bg-canvas">
      <Seo
        title="How TempDash works"
        description="Set up once, then pick up locum shifts near you. See the rate before you accept, clock in on the day, and get paid into your wallet the moment you clock out."
        path="/how-it-works"
        jsonLd={findWorkJsonLd(FAQS)}
      />
      <MarketingNav />

      {/* ⚠️ The ONE main landmark. Without it every <section> on the page
          sits outside any landmark and a screen reader user navigating by
          landmark cannot reach the content at all. The skip link in
          MarketingNav targets this id. */}
      <main id="main" tabIndex={-1} className="outline-none">

      <PageHero
        art={WORKER_SECTION_HERO_XML.ready}
        eyebrow="Find work"
        title="Set up once. Then just pick up work."
        lead="There’s a bit of paperwork at the start, because the places you work in are legally obliged to know who you are. After that there’s none."
      >
        <Button href="/signup?for=work">Join TempDash</Button>
      </PageHero>

      <Section ground="canvas">
        <SectionHead eyebrow="Getting set up" title={<>Once, at the start.</>} />
        <div className="mt-12"><Steps steps={SETUP} /></div>
      </Section>

      <Section ground="surface">
        <SectionHead eyebrow="On the day" title={<>Every time after that.</>} />
        <div className="mt-12">
          <Steps steps={HOW_IT_WORKS_CARDS.map((c) => ({
            art: HOW_IT_WORKS_ART_XML[c.art], title: smart(c.title), body: smart(c.body),
          }))} />
        </div>
      </Section>

      <Section ground="navy">
        <div className="md:flex md:items-center md:gap-16">
          <Art xml={HOW_IT_WORKS_ART_XML.help} className="h-[96px] w-[96px] shrink-0" />
          <div className="mt-6 md:mt-0">
            <H2 size="md">
              If something is not right, you are not on your own.
            </H2>
            <p className="mt-4 max-w-[640px] text-[19px] leading-relaxed text-white/75">
              Tell us on the shift and we get you to the right person. You do not have to argue
              about hours with the workplace, and you do not have to chase anybody for money.
            </p>
          </div>
        </div>
      </Section>

      <Section ground="surface" width="text">
        <H2>Questions, answered.</H2>
        <FaqAccordion items={FAQS} className="mt-10" />
      </Section>

      <CtaBand title="Ready when you are.">
        <Button href="/signup?for=work">Join TempDash</Button>
      </CtaBand>

      </main>

      <MarketingFooter />
    </div>
  )
}
